/**
 * Singleton I18n Service for managing internationalization across all apps
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { I18nConfig, SupportedCurrency, SupportedLanguage } from './types';
import { i18nNamespaces, i18nResources } from './locales/i18n-resources';
import {
  convertToMajorCurrency,
  convertToMinorCurrency,
  formatCompactCurrency,
  formatCurrency,
  getCurrencySymbol,
  getMaxDecimalPlaces,
  isValidDecimalCurrency,
} from './formatters/currency';
import {
  createScopedTranslator,
  exists,
  translate,
} from './formatters/language';
import { CountryCode } from './country-codes';
import { getCountryCode, getDialCode } from './formatters/country-code';

class I18nService {
  private static instance: I18nService;
  private _language: SupportedLanguage = SupportedLanguage.ENGLISH;
  private _currency: SupportedCurrency = 'INR';
  private _timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
  private _defaultCountryCode: CountryCode = 'IN';
  private _countryCode: CountryCode = 'IN';
  private _initialized = false;
  private _changeListeners: Set<() => void> = new Set();

  private constructor() {}

  /**
   * Get singleton instance
   */
  static getInstance(): I18nService {
    if (!I18nService.instance) {
      I18nService.instance = new I18nService();
    }
    return I18nService.instance;
  }

  /**
   * Initialize the i18n service with resources
   * @param config - Initial configuration
   */
  async initialize(config: Partial<I18nConfig>): Promise<void> {
    if (this._initialized) {
      console.warn('I18nService already initialized');
      return;
    }

    await i18n.use(initReactI18next).init({
      resources: i18nResources,
      lng: config?.language || this._language,
      fallbackLng: 'en',
      ns: i18nNamespaces,
      defaultNS: undefined,
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
      },
    });

    if (config?.currency) {
      this._currency = config.currency;
    }
    if (config?.timezone) {
      this._timezone = config.timezone;
    }
    if (config?.language) {
      this._language = config.language;
    }
    if (config?.countryCode) {
      this._defaultCountryCode = config.countryCode;
    }

    this._initialized = true;
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this._initialized;
  }

  /**
   * Reset the service to initial state
   * Clears all settings and marks service as uninitialized
   */
  async reset(): Promise<void> {
    this._language = SupportedLanguage.ENGLISH;
    this._currency = 'INR';
    this._timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this._defaultCountryCode = 'IN';
    this._countryCode = 'IN';
    this._initialized = false;
    this._changeListeners.clear();

    // Reset i18n to default language
    await i18n.changeLanguage('en');

    this._notifyListeners();
  }
  // ==================== Configuration Methods ====================

  /**
   * Get all current settings
   */
  getConfig(): Required<I18nConfig> {
    return {
      language: this._language,
      currency: this._currency,
      timezone: this._timezone,
      countryCode: this._defaultCountryCode,
    };
  }

  /**
   * Update multiple settings at once
   */
  async updateConfig(config: Partial<I18nConfig>): Promise<void> {
    if (config.language && config.language !== this._language) {
      await this.setLanguage(config.language);
    }
    if (config.currency && config.currency !== this._currency) {
      this.setCurrency(config.currency);
    }
    if (config.timezone && config.timezone !== this._timezone) {
      this.setTimezone(config.timezone);
    }
    if (config.countryCode && config.countryCode !== this._defaultCountryCode) {
      this.setDefaultCountryCode(config.countryCode);
    }
  }

  // ==================== Event Listeners ====================

  /**
   * Subscribe to configuration changes
   */
  onChange(callback: () => void): () => void {
    this._changeListeners.add(callback);

    // Return unsubscribe function
    return () => {
      this._changeListeners.delete(callback);
    };
  }

  private _notifyListeners(): void {
    this._changeListeners.forEach((callback) => callback());
  }

  // ==================== Language Methods ====================

  /**
   * Get current language
   */
  getLanguage(): SupportedLanguage {
    return this._language;
  }

  /**
   * Set current language
   */
  async setLanguage(language: SupportedLanguage): Promise<void> {
    this._language = language;
    await i18n.changeLanguage(language);
    this._notifyListeners();
  }

  /**
   * Translate a key
   * @param key - Translation key (e.g., 'chats:welcomeMessage')
   * @param options - Interpolation options
   */
  t(key: string | string[], options?: Record<string, any> | string): string {
    return translate(key, options);
  }

  /**
   * Check if translation key exists
   */
  exists(key: string | string[]): boolean {
    return exists(key);
  }

  /**
   * Create a scoped translation function for a specific namespace
   * @param namespace - The namespace to scope translations to
   * @returns A translation function scoped to the namespace
   *
   * @example
   * const tProduct = i18nService.createScopedTranslator('product');
   * tProduct('title'); // Translates 'product:title'
   * tProduct(['details', 'price']); // Translates 'product:details.price'
   */
  createScopedTranslator(
    namespace: string
  ): (key: string | string[], defaultValue?: string) => string {
    return createScopedTranslator(namespace);
  }

  // ==================== Currency Methods ====================

  /**
   * Get current currency
   */
  getCurrency(): SupportedCurrency {
    return this._currency;
  }

  /**
   * Set current currency
   */
  setCurrency(currency: SupportedCurrency): void {
    this._currency = currency;
    this._notifyListeners();
  }

  /**
   * Format number as currency
   * @param amount - Numeric amount
   */
  formatCurrency(amount: number): string {
    return formatCurrency(amount, this._currency, this.getLocale());
  }

  /**
   * Format number as currency
   * @param amount - Numeric amount
   */
  formatMajorCurrency(amount: number): string {
    const amt = this.convertToMinorCurrency(amount);
    return formatCurrency(amt, this._currency, this.getLocale());
  }

  /**
   * Format number as compact currency
   * @param amount - Numeric amount
   */
  formatCompactCurrency(amount: number): string {
    return formatCompactCurrency(amount, this._currency, this.getLocale());
  }

  /**
   * Format number as currency
   * @param amount - Numeric amount
   */
  formatCompactMajorCurrency(amount: number): string {
    const amt = this.convertToMinorCurrency(amount);
    return formatCompactCurrency(amt, this._currency, this.getLocale());
  }

  /**
   * Convert currency to minor value
   * @param amount - Numeric amount
   */
  convertToMinorCurrency(amount: number): number {
    return convertToMinorCurrency(amount, this._currency, this.getLocale());
  }

  /**
   * Convert currency to minor value
   * @param amount - Numeric amount
   */
  convertToMajorCurrency(amount: number): number {
    return convertToMajorCurrency(amount, this._currency, this.getLocale());
  }

  /**
   * Validate decimal value of currency
   * @param amount - String amount
   */
  isValidDecimalCurrency(amount: string): boolean {
    return isValidDecimalCurrency(amount, this._currency, this.getLocale());
  }

  /**
   * Get currency symbol
   */
  getCurrencySymbol(): string {
    const formatted = getCurrencySymbol(this._currency, this.getLocale());
    return formatted.replace(/\d/g, '').replace(/\s/g, '').trim();
  }

  // ==================== Timezone Methods ====================

  /**
   * Get current timezone
   */
  getTimezone(): string {
    return this._timezone;
  }

  /**
   * Set current timezone
   */
  setTimezone(currency: string): void {
    this._timezone = currency;
    this._notifyListeners();
  }

  /**
   * Get current locale
   */
  getLocale(): string {
    if (this._currency === 'INR') return 'en-IN';
    const formatter = new Intl.DateTimeFormat(undefined, {
      timeZone: this._timezone,
    });
    const locale = formatter.resolvedOptions().locale;
    return locale ?? 'en-US';
  }

  getMaxDecimalPlaces(): number {
    return getMaxDecimalPlaces(this.getCurrency());
  }
  // ==================== Country Code Methods ====================

  /**
   * Get default country code
   */
  getDefaultCountryCode(): string {
    return this._defaultCountryCode;
  }

  /**
   * Set default country code
   */
  setDefaultCountryCode(countryCode: CountryCode): void {
    this._defaultCountryCode = countryCode;
    this._notifyListeners();
  }

  /**
   * Get default country dial code
   */
  getDefaultDialCode(): string {
    return getDialCode(this._defaultCountryCode);
  }

  /**
   * Get current country code
   */
  getCurrentCountryCode(): string {
    return this._countryCode;
  }

  /**
   * Set current country code
   */
  setCurrentCountryCode(countryCode: CountryCode): void {
    this._countryCode = countryCode;
    this._notifyListeners();
  }

  /**
   * Get default country dial code
   */
  getCurrentDialCode(): string {
    return getDialCode(this._countryCode);
  }

  /**
   * Set current country code
   */
  setCurrentDialCode(dialCode: string): void {
    const countryCode = getCountryCode(dialCode);
    this._countryCode = countryCode;
    this._notifyListeners();
  }
}

export const i18nService = I18nService.getInstance();
